import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { Table } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import withAuth from "../../utils/withAuth";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import { fetchCallAllDataByIDAPI, filterCallByParams } from "../../utils/api";
import { numberFormat } from "../../utils/numberFormat";

const ProcessReport = () => {
  const navigate = useNavigate();
  const ppcid = localStorage.getItem("ppcdata");

  const [isLoading, setIsLoading] = useState(false);
  const [process, setProcess] = useState([]);
  const [searchSkillRaw, setSearchSkillRaw] = useState("");
  const [searchSkill, setSearchSkill] = useState("");
  const [isCustomRange, setIsCustomRange] = useState(false);

  const defaultStart = useMemo(() => moment().startOf("month"), []);
  const defaultEnd = useMemo(() => moment().endOf("day"), []);
  const [dateRange, setDateRange] = useState({
    startDate: defaultStart,
    endDate: defaultEnd,
  });

  useEffect(() => {
    const t = setTimeout(() => setSearchSkill(searchSkillRaw.trim()), 250);
    return () => clearTimeout(t);
  }, [searchSkillRaw]);

  const drpRef = useRef(null);

  useEffect(() => {
    if (!ppcid) {
      navigate("/");
      return;
    }
    loadCurrentMonth(ppcid);
  }, [ppcid]);

  const loadCurrentMonth = async (id) => {
    setIsLoading(true);
    try {
      const res = await fetchCallAllDataByIDAPI(
        `ppc-team-get-current-month-process-report/${id}`
      );
      if (res?.data?.status === 1) {
        setProcess(res.data.process || []);
      } else {
        toast.error(res?.data?.message || "Failed to load process");
      }
    } catch (err) {
      console.error(err);
      toast.error("Network/CORS error while loading process");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterByDate = async (_event, picker) => {
    setIsLoading(true);
    const start = picker.startDate.clone().startOf("day");
    const end = picker.endDate.clone().endOf("day");

    setDateRange({ startDate: start, endDate: end });
    setIsCustomRange(true);

    try {
      const res = await filterCallByParams("ppc-team-get-filter-process-report", {
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
        ppcid,
      });
      if (res?.data?.status === 1) setProcess(res.data.process || []);
      if (drpRef.current) {
        drpRef.current.innerText = `${start.format("DD MMM YYYY")} – ${end.format(
          "DD MMM YYYY"
        )}`;
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching filtered report");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = async () => {
    if (drpRef.current) drpRef.current.innerText = "Select Date Range";
    setIsCustomRange(false);
    setDateRange({ startDate: defaultStart, endDate: defaultEnd });
    await loadCurrentMonth(ppcid);
  };

  const filteredProcess = useMemo(() => {
    const q = searchSkill.toLowerCase();
    if (!q) return process;
    return (process || []).filter((item) => {
      const fields = [item?.skillname, item?.display];
      return fields.some((f) => String(f || "").toLowerCase().includes(q));
    });
  }, [process, searchSkill]);

  // ===== Totals & Averages =====
  const totals = useMemo(() => {
    const n = (v) => Number(v) || 0;

    return filteredProcess.reduce(
      (acc, item) => {
        acc.orders_count += n(item.orders_count);
        acc.value += n(item.value);
        acc.delivered_value += n(item.delivered_value);
        acc.delivered_count += n(item.delivered_count);
        acc.calls_unique_skill_server += n(item.calls_unique_skill_server);
        acc.calls_unique_exclusive += n(item.calls_unique_exclusive);

        // NEW: sum of total PPC cost
        acc.ppc_spend_total += n(item.ppc_spend_total);

        // for simple averages
        acc.sum_conv_pct += n(item.conv_pct);
        acc.sum_del_pct += n(item.del_pct);
        acc.sum_tkt_size += n(item.tkt_size);
        acc.rows += 1;

        return acc;
      },
      {
        orders_count: 0,
        value: 0,
        delivered_value: 0,
        delivered_count: 0,
        calls_unique_skill_server: 0,
        calls_unique_exclusive: 0,
        ppc_spend_total: 0,

        sum_conv_pct: 0,
        sum_del_pct: 0,
        sum_tkt_size: 0,
        rows: 0,
      }
    );
  }, [filteredProcess]);

  const avgConv = totals.rows ? totals.sum_conv_pct / totals.rows : 0;
  const avgDel = totals.rows ? totals.sum_del_pct / totals.rows : 0;
  const avgTkt = totals.rows ? totals.sum_tkt_size / totals.rows : 0;

  // NEW: Grand total cost & averages requested
  const grandTotalCost = totals.ppc_spend_total;
  // Avg cost per call = grand total cost / total exclusive calls
  const avgCostPerCall =
    totals.calls_unique_exclusive > 0
      ? grandTotalCost / totals.calls_unique_exclusive
      : 0;
  // Avg factor = Total Value / Grand Total Cost
  const avgFactor = grandTotalCost > 0 ? totals.value / grandTotalCost : 0;

  return (
    <div className="misDashboard">
      <Helmet>
        <title>SATKARTAR::PROCESS REPORT</title>
      </Helmet>
      <ContentWrapper>
        <div className="row">
          <div className="col-12 col-lg-12 col-sm-6">
            <div className="card hrpages">
              <div className="heading p-2 d-flex flex-wrap gap-2 justify-content-between items-center">
                <h5 className="mb-0 text-uppercase" style={{ fontWeight: "600" }}>
                  Process Details (
                  {isCustomRange
                    ? `${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format(
                        "DD MMM YYYY"
                      )}`
                    : "Last 7 Day`s"}
                  )
                </h5>

                <div style={{ width: "25%", minWidth: 260 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search By Skill Name And Display Number "
                    value={searchSkillRaw}
                    onChange={(e) => setSearchSkillRaw(e.target.value)}
                  />
                </div>

                <div className="d-flex gap-2">
                  <DateRangePicker
                    initialSettings={{
                      startDate: dateRange.startDate,
                      endDate: dateRange.endDate,
                      autoUpdateInput: false,
                      showDropdowns: true,
                      alwaysShowCalendars: true,
                      locale: { format: "DD MMM YYYY" },
                    }}
                    onApply={handleFilterByDate}
                  >
                    <button className="btn btn-sm btndaterange" ref={drpRef} type="button">
                      Select Date Range
                    </button>
                  </DateRangePicker>

                  <button
                    className="btn btn-sm btn-reset"
                    type="button"
                    onClick={handleClear}
                    disabled={isLoading}
                    title="Reset to current month"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="table pagetable table-responsive">
                <Table className="table-sm table-fixeds" responsive striped bordered hover>
                  <thead>
                    <tr>
                      {/* span the columns before the two call totals */}
                      <th
                        colSpan={3}
                        className="text-end"
                        style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}
                      >
                        Grand Total
                      </th>

                      {/* Call(Skill) total */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {totals.calls_unique_skill_server}
                      </th>

                      {/* Calls(All Skill) total */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {totals.calls_unique_exclusive}
                      </th>

                      {/* Count of Orders */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {totals.orders_count}
                      </th>

                      {/* Volume */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {numberFormat(totals.value)}
                      </th>

                      {/* Total Cost (Grand total cost) */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {numberFormat(grandTotalCost)}
                      </th>

                      {/* Delivered */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {numberFormat(totals.delivered_value)}
                      </th>

                      {/* Del Count */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {totals.delivered_count}
                      </th>

                      {/* Conv(%) AVG */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {avgConv.toFixed(2)}%
                      </th>

                      {/* Avg Cost/Call = total cost / total exclusive calls */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {numberFormat(avgCostPerCall)}
                      </th>

                      {/* Del(%) AVG */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {avgDel.toFixed(2)}%
                      </th>

                      {/* Ticket Size AVG */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {numberFormat(avgTkt)}
                      </th>

                      {/* Avg Factor = total value / total cost */}
                      <th style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>
                        {avgFactor.toFixed(2)}
                      </th>
                    </tr>
                  </thead>

                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Skill Name</th>
                      <th>Display</th>
                      <th>Call(Skill)</th>
                      <th>Calls(All Skill)</th>
                      <th>Count of Orders</th>
                      <th>Volume</th>
                      <th>Total Cost</th>
                      <th>Delivered</th>
                      <th>Del Count</th>
                      <th>Conv(%)</th>
                      <th>Call/Cost</th>
                      <th>Del(%)</th>
                      <th>Ticket Size</th>
                      <th>Factor</th>
                    </tr>
                  </thead>

                  <tbody>
                    {isLoading ? (
                      [...Array(10)].map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 15 }).map((_, index) => (
                            <td key={index}>
                              <Skeleton animation="wave" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : filteredProcess.length > 0 ? (
                      filteredProcess.map((item, index) => {
                        const ordersCount = Number(item.orders_count) || 0;
                        const callsExclusive = Number(item.calls_unique_exclusive) || 0;
                        const value = Number(item.value) || 0;
                        const totalCost = Number(item.ppc_spend_total) || 0;
                        const costPerCall =
                          callsExclusive > 0 ? totalCost / callsExclusive : 0;
                        const rowFactor = totalCost > 0 ? value / totalCost : 0;

                        return (
                          <tr key={`${item.skillname}-${index}`}>
                            <td>{index + 1}</td>
                            <td><Link to={item.process_id}>{item.skillname}</Link></td>
                            <td>{item.display}</td>
                            <td>{Number(item.calls_unique_skill_server) || 0}</td>
                            <td>{callsExclusive}</td>
                            <td>{ordersCount}</td>
                            <td>{numberFormat(value)}</td>
                            <td>{numberFormat(totalCost)}</td>
                            <td>{numberFormat(Number(item.delivered_value) || 0)}</td>
                            <td>{Number(item.delivered_count) || 0}</td>
                            <td>{Number(item.conv_pct) || 0}</td>
                            <td>{numberFormat(costPerCall)}</td>
                            <td>{Number(item.del_pct) || 0}</td>
                            <td>{numberFormat(Number(item.tkt_size) || 0)}</td>
                            <td>{rowFactor.toFixed(2)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={15} className="text-center">
                          No matching skills found...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>
        {/* End */}
      </ContentWrapper>
    </div>
  );
};

export default withAuth(ProcessReport);
